import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarDays, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { tageBisText } from "@/lib/termine/infoveranstaltungen";
import {
  INFO_DACH,
  INFO_KNOPF,
  kurzTitel,
  leisteAdresse,
  leisteAufPfad,
  leisteLesen,
  leisteTagUhrKnapp,
  leisteWegSchluessel,
  leisteZeitKurz,
  leisteZeitLang,
  popupAufPfad,
  popupGesehenSchluessel,
  POPUP_VERZOEGERUNG_MS,
  type InfoLeisteTermin,
} from "@/lib/termine/infoLeiste";
import { CountdownLeiste } from "./Countdown";

/** Das Pop-up im neuen Design lädt erst, wenn es aufgeht (mit `webseite.css`, nur unter `.mk-webseite`). */
const InfoPopup = lazy(() => import("./InfoPopup"));

// Hinweisleiste für die nächste Infoveranstaltung (Daniel 22.10.2026): der Streifen über dem Menü der Webseite (alter
// Kopf `Header.tsx` und neuer Kopf `webseite/Kopf.tsx`, Daniel: „Die Info-Leiste stelle ich mir über dem Menü vor“) und
// der Hinweis in der Übersicht des Mitgliederbereichs (gewohnte Übersicht und neue Ansicht). Welches Thema dort steht,
// schaltet die Administration je Thema; gelesen wird einmal je Seitenaufruf über `marketing_info_hinweis()`. Ohne
// Antwort, in der App, auf der Landingpage selbst und nachdem jemand das X gedrückt hat, zeichnet nichts etwas (der alte
// Kopf bleibt dann Zeichen für Zeichen gleich). Die Leiste ist orange (Daniel 22.10.2026: „immer noch nicht orange,
// sondern türkis“; Türkis steht auf der Seite schon genug). Mit dem Schalter „Countdown“ wird sie etwas höher und zählt
// bis zum Beginn herunter, mit „Pop-up“ geht einmal je Termin ein Fenster im neuen Design mit Anmeldung auf (Daniel
// 22.10.2026: „Irgendwie muss klar werden: Wir ziehen um“). Über dem Satz steht immer, auch am Handy, „Kostenlose
// Infoveranstaltung“ mit Tag und Uhrzeit, der Knopf heißt „Kostenlos anmelden“ (Daniel 23.10.2026: „Da ist gar nicht
// klar, dass es ein Infonar ist“). Regeln: docs/regeln/admin-dashboard.md „Hinweisleiste“.

let anfrage: Promise<InfoLeisteTermin | null> | null = null;

function leisteLaden(): Promise<InfoLeisteTermin | null> {
  anfrage ??= Promise.resolve(supabase.rpc("marketing_info_hinweis"))
    .then((r) => (r && !r.error ? leisteLesen(r.data) : null))
    .catch(() => null);
  return anfrage;
}

/** Wer gerade zuhört, wenn jemand das X drückt (Streifen und Platzhalter verschwinden zusammen). */
const hoerer = new Set<() => void>();
const wegInDieserSitzung = new Set<string>();

function istWeg(terminId: string): boolean {
  if (wegInDieserSitzung.has(terminId)) return true;
  try {
    return window.localStorage.getItem(leisteWegSchluessel(terminId)) === "1";
  } catch {
    return false;
  }
}

function wegMerken(terminId: string) {
  wegInDieserSitzung.add(terminId);
  try {
    window.localStorage.setItem(leisteWegSchluessel(terminId), "1");
  } catch {
    // ohne Speicher im Browser bleibt sie nur bis zum nächsten Aufruf weg
  }
  hoerer.forEach((h) => h());
}

function useInfoLeiste(aktiv: boolean) {
  const [termin, setTermin] = useState<InfoLeisteTermin | null>(null);
  const [weg, setWeg] = useState(false);
  useEffect(() => {
    if (!aktiv) return;
    let lebt = true;
    let id: string | null = null;
    const pruefen = () => {
      if (lebt && id) setWeg(istWeg(id));
    };
    hoerer.add(pruefen);
    leisteLaden().then((t) => {
      if (!lebt || !t) return;
      id = t.termin_id;
      setWeg(istWeg(t.termin_id));
      setTermin(t);
    });
    return () => {
      lebt = false;
      hoerer.delete(pruefen);
    };
  }, [aktiv]);
  const schliessen = () => {
    if (termin) wegMerken(termin.termin_id);
  };
  return { termin: aktiv && !weg ? termin : null, schliessen };
}

/** Steht die Leiste auf dieser Seite? (nicht in der App, nicht auf der Landingpage selbst) */
function useLeisteAufDerSeite() {
  const { pathname } = useLocation();
  const istApp = useIsNativeApp();
  return useInfoLeiste(!istApp && leisteAufPfad(pathname));
}

/**
 * Höhe des Streifens: am Handy Dachzeile und Satz, mit Countdown darunter Countdown und Knopf; am Rechner eine Reihe,
 * mit Countdown etwas höher. Der Platzhalter nimmt dieselbe.
 */
const hoehe = (t: InfoLeisteTermin) => (t.countdown ? "h-[92px] sm:h-14" : "h-[60px] sm:h-10");

const KNOPF =
  "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1.5 sm:px-3 text-[13px] font-bold text-[#1D2934] shadow-[0_1px_2px_rgba(29,41,52,0.12)] transition-colors group-hover:bg-[#FFF6E6]";
const LINK =
  "group !text-[#1D2934] no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D2934]";

/**
 * Was es ist und wann, über dem Satz: „Kostenlose Infoveranstaltung · 2. Nov., 20 Uhr“. Am Handy so hoch wie das X
 * rechts daneben (24 px), damit der Satz darunter die ganze Breite hat. `[display:block]` statt `block`: Die neue
 * Webseite hat eine eigene Klasse `.block` mit Abstand nach oben (`webseite.css`), die Leiste steht auch dort.
 */
function Dach({ beginn }: { beginn: string }) {
  return (
    <span className="truncate pr-8 text-[11px] [display:block] leading-6 sm:pr-0 sm:text-[12px] sm:leading-4">
      <span className="font-extrabold uppercase sm:tracking-[0.04em]">{INFO_DACH}</span>
      <span className="sm:hidden"> · {leisteTagUhrKnapp(beginn)}</span>
      <span className="hidden sm:inline"> · {leisteZeitKurz(beginn)} · live über Zoom</span>
    </span>
  );
}

/**
 * Der Streifen über dem Menü, als erstes Kind im festen Kopf (alt `<header className="fixed …">`, neu `.kopf`): Er
 * wandert mit dem Kopf und macht ihn höher. Den Platz darunter schafft `InfoLeisteAbstand` nach dem Kopf.
 */
export function InfoLeiste() {
  const { termin, schliessen } = useLeisteAufDerSeite();
  if (!termin) return null;
  const kurz = kurzTitel(termin.titel);
  const knopf = (
    <span className={KNOPF}>
      {INFO_KNOPF}
      <ArrowRight className="hidden h-3.5 w-3.5 sm:block" aria-hidden="true" />
    </span>
  );
  return (
    <div role="region" aria-label="Hinweis auf die nächste Infoveranstaltung" className="bg-[#F5A623] text-[#1D2934]">
      <div className={`relative mx-auto max-w-6xl ${hoehe(termin)}`}>
        {termin.countdown ? (
          // Mit Countdown (Daniel: „Die Leiste kann oben ausnahmsweise ein bisschen [größer sein], weil es ja schon eine
          // besondere Veranstaltung ist“): am Rechner eine Reihe, am Handy drei Zeilen: Dachzeile mit dem X, der Satz,
          // Countdown und Knopf, jede über die ganze Breite.
          <Link
            to={leisteAdresse(termin, "leiste")}
            className={`${LINK} flex h-full flex-col justify-start gap-1 px-4 pt-1 sm:flex-row sm:items-center sm:justify-center sm:gap-5 sm:px-12 sm:pt-0`}
          >
            <span className="min-w-0">
              <Dach beginn={termin.beginn} />
              <strong className="truncate py-0.5 text-[13.5px] [display:block] font-extrabold leading-snug sm:text-[15px]">
                {termin.leiste_text ?? termin.titel}
              </strong>
            </span>
            <span className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
              <CountdownLeiste beginn={termin.beginn} />
              {knopf}
            </span>
          </Link>
        ) : (
          <Link
            to={leisteAdresse(termin, "leiste")}
            className={`${LINK} flex h-full min-w-0 flex-col justify-start gap-0.5 px-4 pt-1 sm:flex-row sm:pt-0 sm:items-center sm:justify-center sm:gap-2.5 sm:px-12`}
          >
            <span className="sm:hidden">
              <Dach beginn={termin.beginn} />
            </span>
            <span className="hidden shrink-0 rounded-full bg-white/45 px-2.5 py-1 text-[11px] font-bold uppercase leading-none tracking-[0.08em] sm:inline">
              {INFO_DACH}
            </span>
            <span className="flex min-w-0 items-center gap-2.5">
              <span className="min-w-0 flex-1 truncate py-0.5 text-[13px] leading-snug sm:flex-initial sm:text-[14px]">
                <strong className="font-bold lg:hidden">{termin.leiste_text ?? kurz}</strong>
                <strong className="hidden font-bold lg:inline">{termin.leiste_text ?? termin.titel}</strong>
                <span className="hidden sm:inline"> · {leisteZeitKurz(termin.beginn)}</span>
              </span>
              {knopf}
            </span>
          </Link>
        )}
        <button
          type="button"
          onClick={schliessen}
          aria-label="Hinweis schließen"
          className="absolute right-1.5 top-1 flex h-6 w-6 items-center justify-center rounded-full sm:h-8 sm:w-8 text-[#1D2934]/75 hover:bg-white/40 hover:text-[#1D2934] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1D2934] sm:top-1/2 sm:-translate-y-1/2"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Platz unter dem festen Kopf, solange der Streifen darin steht (so hoch wie der Streifen). */
export function InfoLeisteAbstand() {
  const { termin } = useLeisteAufDerSeite();
  return termin ? <div className={hoehe(termin)} aria-hidden="true" /> : null;
}

function popupGesehen(terminId: string): boolean {
  try {
    return window.localStorage.getItem(popupGesehenSchluessel(terminId)) === "1";
  } catch {
    // ohne Speicher im Browser lieber gar nicht als bei jedem Seitenwechsel
    return true;
  }
}

/**
 * Das Pop-up (Schalter „Pop-up“ je Thema): geht einmal je Termin und Browser auf, sechs Sekunden nachdem eine Seite
 * steht, nie in der App, nie auf der Landingpage, beim Anmelden, in Kassen, auf Dankeseiten und Rechtstexten
 * (`popupAufPfad()`). Wer die Leiste schon weggeklickt hat, bekommt auch kein Pop-up. Steht im Kopf neben
 * `InfoLeisteAbstand`.
 */
export function InfoPopupSteuerung() {
  const { pathname } = useLocation();
  const istApp = useIsNativeApp();
  const { termin } = useInfoLeiste(!istApp);
  const [offen, setOffen] = useState(false);
  const erlaubt = !!termin?.popup && popupAufPfad(pathname);
  const terminId = termin?.termin_id;
  useEffect(() => {
    if (!erlaubt || !terminId || offen || popupGesehen(terminId)) return;
    const zeit = window.setTimeout(() => {
      try {
        window.localStorage.setItem(popupGesehenSchluessel(terminId), "1");
      } catch {
        // dann eben nur dieses eine Mal
      }
      setOffen(true);
    }, POPUP_VERZOEGERUNG_MS);
    return () => window.clearTimeout(zeit);
  }, [erlaubt, terminId, offen]);
  if (!offen || !termin) return null;
  return (
    <Suspense fallback={null}>
      <InfoPopup termin={termin} schliessen={() => setOffen(false)} />
    </Suspense>
  );
}

/**
 * Der Hinweis in der Übersicht des Mitgliederbereichs. Wer schon angemeldet ist (`marketing_info_meine()`), sieht ihn
 * nicht mehr; das X gilt wie beim Streifen für diesen Termin.
 */
export function InfoHinweisMitglieder() {
  const istApp = useIsNativeApp();
  const { termin, schliessen } = useInfoLeiste(!istApp);
  const [angemeldet, setAngemeldet] = useState<boolean | null>(null);
  const terminId = termin?.termin_id;
  useEffect(() => {
    if (!terminId) return;
    let lebt = true;
    Promise.resolve(supabase.rpc("marketing_info_meine"))
      .then((r) => {
        const zeilen = r && !r.error && Array.isArray(r.data) ? (r.data as { termin_id?: string }[]) : [];
        if (lebt) setAngemeldet(zeilen.some((z) => z.termin_id === terminId));
      })
      .catch(() => lebt && setAngemeldet(false));
    return () => {
      lebt = false;
    };
  }, [terminId]);
  if (!termin || angemeldet !== false) return null;
  const bald = tageBisText(termin.beginn);
  return (
    <aside
      aria-label="Hinweis auf die nächste Infoveranstaltung"
      className="relative flex flex-col gap-4 rounded-2xl border-2 border-[#49BDC2] bg-[#EEF8F8] p-4 pr-12 sm:flex-row sm:items-center sm:gap-5 sm:p-5 sm:pr-14"
    >
      <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#227277] text-white sm:flex" aria-hidden="true">
        <CalendarDays className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#227277]">
          {INFO_DACH}{bald ? ` · ${bald}` : ""}
        </p>
        <p className="mt-1 text-[17px] font-bold leading-snug text-[#1D2934] [text-wrap:balance]">{termin.titel}</p>
        <p className="mt-1 text-[14px] text-[#4A5560]">{leisteZeitLang(termin.beginn)} · online über Zoom</p>
      </div>
      <Link
        to={leisteAdresse(termin, "mitgliederbereich")}
        className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full bg-[#F5A623] px-5 py-2.5 text-[15px] font-bold !text-white no-underline transition-colors hover:bg-[#E89B17] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#227277] sm:self-center"
      >
        {INFO_KNOPF}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
      <button
        type="button"
        onClick={schliessen}
        aria-label="Hinweis schließen"
        className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full text-[#4A5560] hover:bg-white hover:text-[#1D2934] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#227277]"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}
